import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plane, Search, Plus, CheckCircle, FileCheck, Package, ClipboardCheck, PlaneTakeoff, PackageCheck } from 'lucide-react';
import { apiGet, apiPost } from './appApi';
import { formatDateShort } from '../../utils/dateFormat';
import { logger } from '../../utils/logger';
import { usePullToRefresh, PullToRefreshIndicator } from '../../hooks/usePullToRefresh';
import { SkeletonRelocationCard } from '../../components/app/Skeleton';
import { SwipeActionRow } from '../../components/app/SwipeActionRow';

// Status badges — keyed by backend RelocationStatus enum values plus legacy strings
// seen in live data ('pending', 'documentation').
const STATUS_BADGES = {
  pending: { label: 'Pending', class: 'bg-gray-100 text-gray-700' },
  quote_sent: { label: 'Quote', class: 'bg-blue-100 text-blue-700' },
  booking_confirmed: { label: 'Booked', class: 'bg-violet-100 text-violet-700' },
  documentation: { label: 'Docs', class: 'bg-amber-100 text-amber-700' },
  documents_required: { label: 'Docs Req', class: 'bg-amber-100 text-amber-700' },
  documents_submitted: { label: 'Submitted', class: 'bg-indigo-100 text-indigo-700' },
  documents_under_review: { label: 'Reviewing', class: 'bg-indigo-100 text-indigo-700' },
  documents_approved: { label: 'Approved', class: 'bg-emerald-100 text-emerald-700' },
  travel_arrangements: { label: 'Arranging', class: 'bg-sky-100 text-sky-700' },
  flight_booked: { label: 'Flight Booked', class: 'bg-teal-100 text-teal-700' },
  pre_travel_prep: { label: 'Prep', class: 'bg-cyan-100 text-cyan-700' },
  in_transit: { label: 'In Transit', class: 'bg-cyan-100 text-cyan-700' },
  delivered: { label: 'Delivered', class: 'bg-emerald-100 text-emerald-700' },
  complete: { label: 'Complete', class: 'bg-gray-200 text-gray-700' },
};

// Swipe-to-update quick milestone actions — maps each backend status to its
// canonical "next step" button(s). Uses real RelocationStatus enum values
// (/app/backend/models.py → RelocationStatus) so POST /milestone always succeeds.
const NEXT_STATUS_ACTIONS = {
  pending: [
    { status: 'booking_confirmed', label: 'Confirm', color: 'blue', icon: ClipboardCheck },
  ],
  quote_sent: [
    { status: 'booking_confirmed', label: 'Confirm', color: 'blue', icon: ClipboardCheck },
  ],
  booking_confirmed: [
    { status: 'documents_required', label: 'Req Docs', color: 'amber', icon: FileCheck },
  ],
  documentation: [
    { status: 'documents_under_review', label: 'Review', color: 'blue', icon: FileCheck },
  ],
  documents_required: [
    { status: 'documents_submitted', label: 'Submitted', color: 'blue', icon: FileCheck },
  ],
  documents_submitted: [
    { status: 'documents_approved', label: 'Approve', color: 'green', icon: CheckCircle },
  ],
  documents_under_review: [
    { status: 'documents_approved', label: 'Approve', color: 'green', icon: CheckCircle },
  ],
  documents_approved: [
    { status: 'flight_booked', label: 'Book Flight', color: 'primary', icon: Package },
  ],
  travel_arrangements: [
    { status: 'flight_booked', label: 'Book Flight', color: 'primary', icon: Package },
  ],
  flight_booked: [
    { status: 'in_transit', label: 'Depart', color: 'blue', icon: PlaneTakeoff },
  ],
  pre_travel_prep: [
    { status: 'in_transit', label: 'Depart', color: 'blue', icon: PlaneTakeoff },
  ],
  in_transit: [
    { status: 'delivered', label: 'Delivered', color: 'green', icon: PackageCheck },
  ],
  delivered: [
    { status: 'complete', label: 'Complete', color: 'green', icon: CheckCircle },
  ],
};

export const AppRelocations = () => {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [offline, setOffline] = useState(!navigator.onLine);

  const load = useCallback(async () => {
    try {
      const data = await apiGet('/api/admin/relocations');
      setList(data);
      setOffline(false);
    } catch (err) {
      logger.error('Load relocations:', err);
      setOffline(true);
    }
  }, []);

  useEffect(() => {
    load().finally(() => setLoading(false));
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, [load]);

  const { containerRef, pullDistance, isRefreshing } = usePullToRefresh(load);

  const q = query.toLowerCase();
  const filtered = list.filter(r =>
    !q ||
    (r.pet_name || '').toLowerCase().includes(q) ||
    (r.client_name || '').toLowerCase().includes(q) ||
    (r.destination || '').toLowerCase().includes(q) ||
    (r.origin || '').toLowerCase().includes(q)
  );

  const updateMilestone = async (relocation, newStatus, label) => {
    // Optimistic update
    setList((prev) => prev.map(r => r.id === relocation.id ? { ...r, current_status: newStatus } : r));
    try {
      await apiPost(`/api/admin/relocations/${relocation.id}/milestone`, {
        status: newStatus,
        notes: `Marked ${label} via mobile`,
        completed_by: 'Admin',
      });
    } catch (err) {
      logger.error('Milestone update failed', err);
      // Revert on failure
      setList((prev) => prev.map(r => r.id === relocation.id ? { ...r, current_status: relocation.current_status } : r));
      alert(`Failed to update: ${err.message}`);
    }
  };

  return (
    <div ref={containerRef}>
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div
          className="px-4 pb-3"
          style={{ paddingTop: 'calc(env(safe-area-inset-top) + 16px)' }}
        >
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-xl font-bold text-gray-900">Pet Relocations</h1>
            <button
              onClick={() => navigate('/admin/relocations/new')}
              className="w-9 h-9 rounded-full bg-teal-600 text-white flex items-center justify-center active:scale-95"
              data-testid="new-relocation-btn"
              aria-label="New relocation"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              placeholder="Search pets, clients, destinations"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 text-sm bg-gray-100 rounded-xl border-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          {offline && (
            <p className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1" data-testid="offline-banner">
              You're offline — showing last cached data.
            </p>
          )}
        </div>
      </div>

      <PullToRefreshIndicator distance={pullDistance} refreshing={isRefreshing} />

      <div className="px-4 py-3 space-y-2">
        {loading ? (
          [0, 1, 2, 3].map(i => <SkeletonRelocationCard key={i} />)
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Plane className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">
              {query ? 'No matches' : 'No relocations yet'}
            </p>
          </div>
        ) : (
          filtered.map(r => {
            const badge = STATUS_BADGES[r.current_status] || { label: r.current_status || 'Pending', class: 'bg-gray-100 text-gray-700' };
            const nextActions = NEXT_STATUS_ACTIONS[r.current_status] || [];

            const swipeActions = nextActions.map((a) => ({
              icon: a.icon,
              label: a.label,
              color: a.color,
              testId: `swipe-${r.id}-${a.status}`,
              onClick: () => updateMilestone(r, a.status, a.label),
            }));

            return (
              <SwipeActionRow
                key={r.id}
                testId={`relocation-row-${r.id}`}
                actions={swipeActions}
              >
                <button
                  onClick={() => navigate(`/admin/relocations/${r.id}`)}
                  className="w-full bg-white border border-gray-200 rounded-xl p-3 text-left active:bg-gray-50"
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="font-semibold text-gray-900 truncate">{r.pet_name}</p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${badge.class}`}>
                      {badge.label}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 truncate">
                    {r.client_name} · {r.origin} → {r.destination}
                  </p>
                  {r.travel_date && (
                    <p className="text-xs text-teal-600 mt-1 font-medium">
                      ✈︎ {formatDateShort(r.travel_date)}
                    </p>
                  )}
                  {swipeActions.length > 0 && (
                    <p className="text-[10px] text-gray-400 mt-1">← swipe for quick update</p>
                  )}
                </button>
              </SwipeActionRow>
            );
          })
        )}
      </div>
    </div>
  );
};
